import { useState, useEffect } from "react";
import { X, Divide, Calculator, Trophy, BookOpen, Zap, Target } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import RoadmapTile from "../components/RoadmapTile";
import { api } from "../lib/api";
import { featureFlags, userHasPremiumAccess } from "../config/featureFlags";

interface RoadmapProgress {
  roadmapId: string;
  completed: number;
  total: number;
  percentage: number;
  modulesCount: number;
}

export default function Arena() {
  const { user } = useAuth();
  const [progress, setProgress] = useState<Map<string, RoadmapProgress>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);

  // Check if user is a teacher (not a student)
  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      // Fetch all roadmaps with progress in a single optimized call
      const roadmaps = await api.getRoadmaps(true);
      const progressMap = new Map<string, RoadmapProgress>();

      for (const roadmap of roadmaps) {
        // Calculate progress from already-loaded modules and lessons
        let totalLessons = 0;
        let completedLessons = 0;
        const modulesCount = roadmap.modules?.length || 0;

        if (roadmap.modules) {
          for (const module of roadmap.modules) {
            if (module.lessons) {
              totalLessons += module.lessons.length;
              completedLessons += module.lessons.filter(
                (lesson) => lesson.progress?.status === "completed",
              ).length;
            }
          }
        }

        const percentage =
          totalLessons > 0
            ? Math.round((completedLessons / totalLessons) * 100)
            : 0;

        progressMap.set(roadmap.slug, {
          roadmapId: roadmap.id,
          completed: completedLessons,
          total: totalLessons,
          percentage,
          modulesCount,
        });
      }

      setProgress(progressMap);
    } catch (error) {
      console.error("Failed to load roadmap progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const multiplicationProgress = progress.get("multiplication-mastery");
  const specialCasesProgress = progress.get("multiplication-special-cases");
  const divisibilityProgress = progress.get("divisibility-rules-mastery");
  const mentalDivisionProgress = progress.get("mental-division-techniques");
  const ithopuProgress = progress.get("addition-subtraction-mastery");
  const olympiadProgress = progress.get("olympiad-path");
  const satProgress = progress.get("sat-math-speed-track");
  const essentialMathProgress = progress.get("essential-mental-math");

  const isPremiumUser = userHasPremiumAccess(user);

  const roadmaps = [
    {
      title: "Addition & Subtraction",
      description:
        "Build a strong foundation with efficient addition and subtraction methods used in competitive math.",
      icon: Calculator,
      iconColor: "text-sky-600",
      bgGradient: "bg-gradient-to-br from-sky-400 to-blue-600",
      route: "/arena/ithopu-curriculum",
      progress: ithopuProgress,
      isPremium: false,
      stats: [
        { label: "Techniques", value: 10 },
        { label: "Modules", value: 5 },
      ],
    },
    {
      title: "Multiplication Mastery",
      description:
        "Master multiplication techniques from basics to advanced tricks. Learn Vedic methods, special cases, and speed calculation.",
      icon: X,
      iconColor: "text-violet-600",
      bgGradient: "bg-gradient-to-br from-violet-400 to-purple-600",
      route: "/arena/multiplication-mastery",
      progress: multiplicationProgress,
      isPremium: true,
      stats: [
        { label: "Techniques", value: 12 },
        { label: "Modules", value: 8 },
      ],
    },
    {
      title: "Multiplication Special Cases",
      description:
        "Master specialized multiplication techniques for specific number patterns and cases.",
      icon: Zap,
      iconColor: "text-orange-500",
      bgGradient: "bg-gradient-to-br from-amber-400 to-orange-500",
      route: "/arena/multiplication-special-cases",
      progress: specialCasesProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 5 },
        { label: "Modules", value: 5 },
      ],
    },
    {
      title: "Mental Division Techniques",
      description:
        "Master mental calculation methods for dividing numbers in your head — from simple halving to advanced Vedic methods.",
      icon: Divide,
      iconColor: "text-emerald-600",
      bgGradient: "bg-gradient-to-br from-emerald-400 to-green-600",
      route: "/arena/mental-division-techniques",
      progress: mentalDivisionProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 12 },
        { label: "Modules", value: 12 },
      ],
    },
    {
      title: "Divisibility Rules Mastery",
      description:
        "Master pattern recognition for quickly determining if any number is divisible by 2–12 without calculation.",
      icon: Calculator,
      iconColor: "text-rose-500",
      bgGradient: "bg-gradient-to-br from-rose-400 to-pink-600",
      route: "/arena/divisibility-rules-mastery",
      progress: divisibilityProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 11 },
        { label: "Modules", value: 11 },
      ],
    },
    {
      title: "Essential Mental Math",
      description:
        "Real-world shortcuts, Vedic algebra, calculus tricks, competition strategies, geometry, and Russian math fundamentals.",
      icon: Zap,
      iconColor: "text-teal-600",
      bgGradient: "bg-gradient-to-br from-cyan-400 to-teal-600",
      route: "/arena/essential-mental-math",
      progress: essentialMathProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 32 },
        { label: "Modules", value: 6 },
      ],
    },
    {
      title: "SAT Math Speed Track",
      description:
        "Bank time on every SAT Math question. Speed techniques for algebra, advanced math, data analysis, and geometry.",
      icon: Target,
      iconColor: "text-blue-700",
      bgGradient: "bg-gradient-to-br from-blue-600 to-indigo-800",
      route: "/arena/sat-math-speed-track",
      progress: satProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 23 },
        { label: "Modules", value: 11 },
      ],
    },
    {
      title: "The Olympiad Path",
      description:
        "Master competitive mathematics through strategic problem-solving, number theory, algebra, and geometric reasoning.",
      icon: Trophy,
      iconColor: "text-indigo-600",
      bgGradient: "bg-gradient-to-br from-indigo-400 to-blue-700",
      route: "/arena/olympiad-path",
      progress: olympiadProgress,
      isPremium: true,
      badge: "New",
      stats: [
        { label: "Techniques", value: 22 },
        { label: "Modules", value: 6 },
      ],
    },
  ];

  const teacherRoadmaps = [
    {
      title: "Learning Hub",
      description:
        "Browse all available techniques, filter by category, and explore the complete technique library.",
      icon: BookOpen,
      iconColor: "text-slate-700",
      bgGradient: "bg-gradient-to-br from-slate-500 to-gray-700",
      route: "/arena/learning-hub",
      progress: undefined,
      isPremium: false,
      badge: undefined,
      stats: [
        { label: "Techniques", value: "90+" },
        { label: "Categories", value: 5 },
      ],
    },
  ];

  const allRoadmaps = isTeacher ? [...roadmaps, ...teacherRoadmaps] : roadmaps;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            The Arena
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Choose your learning path and start mastering mental math techniques
          </p>
        </div>

        {/* Welcome Message */}
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Welcome{user ? `, ${user.first_name || user.username || user.email.split("@")[0]}` : ""}! 👋
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Ready to level up your mental math skills? Each roadmap is designed
            to take you from beginner to master. Pick a path below and start
            your journey to becoming a calculation champion!
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-96 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Quick Stats Section */}
        {!loading && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Trophy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Total Progress
                </h3>
              </div>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round(
                  Array.from(progress.values()).reduce(
                    (sum, p) => sum + p.percentage,
                    0,
                  ) / (progress.size || 1),
                )}
                %
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Across all roadmaps
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <BookOpen className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Techniques Available
                </h3>
              </div>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                90+
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Vedic, Russian & more
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Calculator className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Practice Modes
                </h3>
              </div>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                4
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Drill, Mixed, Timed & Papers
              </p>
            </div>
          </div>
        )}

        {/* Roadmap Tiles */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allRoadmaps.map((roadmap, index) => (
              <RoadmapTile
                key={index}
                title={roadmap.title}
                description={roadmap.description}
                icon={roadmap.icon}
                iconColor={roadmap.iconColor}
                bgGradient={roadmap.bgGradient}
                route={roadmap.route}
                progress={roadmap.progress}
                stats={roadmap.stats}
                badge={roadmap.badge}
                isPremium={featureFlags.SHOW_PRO_BADGES && roadmap.isPremium && !isPremiumUser}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
